import React, { FC, useMemo } from 'react';

import BasicModal from '../../components/modals/basicModal';
import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';
import { FormulaType } from '../../constants/formulaType';
import ICONS from '../../constants/icons';

import { FormulaTypesData } from '../../models/formula.model';

import ComposerNxChannelGrid from './composerNXModals/ComposerNxChannelGrid';
import ComposerNxChannelSelection from './composerNXModals/ComposerNxChannelSelection';

interface ComposerNXFormulaTypeSelectionModalProps {
    handleGoBack: () => void;
    handleContinueClick: (selectedFormulaType: FormulaType) => void;
    handleTryComposerNX: () => void;
}

const ComposerNXFormulaTypeSelectionModal: FC<
    React.PropsWithChildren<ComposerNXFormulaTypeSelectionModalProps>
> = ({ handleGoBack, handleContinueClick, handleTryComposerNX }) => {
    const otherFormulaTypes = useMemo(
        () =>
            Object.values(FormulaType).filter(
                (type) =>
                    type !== FormulaType.mobilePush &&
                    FormulaTypesData[type]?.standaloneMessages,
            ),
        [],
    );

    return (
        <BasicModal
            childContainerStyle={{ padding: aampeTheme.spacing4(6) }}
            containerBodyStyle={{ borderRadius: '12px' }}
            width={'420px'}
        >
            <MainContainer>
                <HeaderText>Select Channel</HeaderText>

                <ChannelsContainer>
                    <ComposerNxChannelSelection
                        icon={<ICONS.mobilePush />}
                        channelName="Mobile Push"
                        onExistingClick={() =>
                            handleContinueClick(FormulaType.mobilePush)
                        }
                        onComposerNXClick={handleTryComposerNX}
                    />

                    <ComposerNxChannelGrid
                        channels={otherFormulaTypes}
                        onChannelSelect={handleContinueClick}
                    />
                </ChannelsContainer>

                <CancelButton
                    onClick={handleGoBack}
                    aria-label="Cancel channel selection and go back"
                >
                    Cancel
                </CancelButton>
            </MainContainer>
        </BasicModal>
    );
};

const MainContainer = styled.div(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(6),
}));

const HeaderText = styled.h2(() => ({
    color: aampeTheme.colors.neutral09,
    fontSize: aampeTheme.size.font.text,
    fontWeight: aampeTheme.weights.heavy,
    margin: 0,
}));

const ChannelsContainer = styled.div(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(3),
}));

const CancelButton = styled.button(() => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'flex-end',
    padding: aampeTheme.spacing(1, 2),
    border: `1px solid ${aampeTheme.colors.solidA01}`,
    borderRadius: aampeTheme.borderRadius.medium,
    backgroundColor: 'transparent',
    color: aampeTheme.colors.solidA01,
    fontSize: aampeTheme.size.font.textMedium,
    fontWeight: aampeTheme.weights.medium,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: aampeTheme.colors.backgroundWG01,
    },
    '&:focus-visible': {
        outline: `2px solid ${aampeTheme.colors.solidA01}`,
    },
}));

export default ComposerNXFormulaTypeSelectionModal;
